/**
 * MultiFilterSelector Component
 *
 * Unified component for multiple filter types.
 */

import { useState, useRef, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search, X, ChevronRight, CircleDashed, User, Tag, Flag, SlidersHorizontal } from 'lucide-react';
import clsx from 'clsx';
import { useClickOutside } from '@/hooks/useClickOutside';
import { clampToViewport } from '@/hooks/useDropdownPosition';
import { InlineDatePicker } from '@/components/shared/inline-editors';
import { DropdownShell } from './DropdownShell';
import type { CustomFieldValue } from '@/types';

export interface CustomFieldFilterSection {
  fieldId: string;
  label: string;
  type: 'text' | 'date' | 'dropdown' | 'boolean' | 'number';
  options: Array<{ value: string; label: string; icon?: ReactNode }>;
}

export interface MultiFilterSelectorProps {
  filters: {
    status: string[];
    assignees: string[];
    tags: string[];
    priority: string[];
  };
  customFieldFilters?: Record<string, CustomFieldValue[]>;
  customFieldTextFilters?: Record<string, string>;
  customFieldDateRangeFilters?: Record<string, { start: string | null; end: string | null }>;
  onFilterChange: (
    filters: Partial<{
      status: string[];
      assignees: string[];
      tags: string[];
      priority: string[];
      customFields: Record<string, CustomFieldValue[]>;
      customFieldText: Record<string, string>;
      customFieldDateRange: Record<string, { start: string | null; end: string | null }>;
    }>
  ) => void;
  compact?: boolean;
  availableOptions: {
    status: Array<{ value: string; label: string; icon?: ReactNode }>;
    assignees: Array<{ value: string; label: string; icon?: ReactNode }>;
    tags: Array<{ value: string; label: string; icon?: ReactNode }>;
    priority: Array<{ value: string; label: string; icon?: ReactNode }>;
  };
  customFieldSections?: CustomFieldFilterSection[];
  tagGroups?: Array<{ label: string; options: Array<{ value: string; label: string; icon?: ReactNode }> }>;
  icon: ReactNode;
}

type SectionKey = 'status' | 'assignees' | 'tags' | 'priority';

export function MultiFilterSelector({
  filters,
  customFieldFilters,
  customFieldTextFilters,
  customFieldDateRangeFilters,
  onFilterChange,
  availableOptions,
  customFieldSections = [],
  tagGroups,
  icon,
  compact = false,
}: MultiFilterSelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({
    status: true,
    assignees: true,
    tags: true,
    priority: true,
  });

  useClickOutside(
    [containerRef, dropdownRef],
    () => {
      setOpen(false);
      setQuery('');
      setDropdownPosition(null);
    },
    open
  );

  useEffect(() => {
    if (open && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const width = Math.max(rect.width, 280);
      const pos = clampToViewport(rect.left, rect.bottom + 6, width, 300);
      setDropdownPosition({ ...pos, width });
    } else {
      setDropdownPosition(null);
    }
  }, [open]);

  const cfSelectCount = customFieldFilters
    ? Object.values(customFieldFilters).reduce((sum, vals) => sum + (vals?.length || 0), 0)
    : 0;
  const cfTextCount = customFieldTextFilters
    ? Object.values(customFieldTextFilters).filter(v => v.length > 0).length
    : 0;
  const cfDateCount = customFieldDateRangeFilters
    ? Object.values(customFieldDateRangeFilters).filter(v => v.start || v.end).length
    : 0;
  const cfActiveCount = cfSelectCount + cfTextCount + cfDateCount;

  const totalActiveFilters =
    filters.status.length +
    filters.assignees.length +
    filters.tags.length +
    filters.priority.length +
    cfActiveCount;

  useEffect(() => {
    if (!open) return;
    const shouldExpandAllForSearch = query.trim().length > 0;
    const collapsed: Record<string, boolean> = {
      status: !(shouldExpandAllForSearch || filters.status.length > 0),
      assignees: !(shouldExpandAllForSearch || filters.assignees.length > 0),
      tags: !(shouldExpandAllForSearch || filters.tags.length > 0),
      priority: !(shouldExpandAllForSearch || filters.priority.length > 0),
    };
    for (const cf of customFieldSections) {
      const cfActive = customFieldFilters?.[cf.fieldId]?.length || 0;
      collapsed[`cf_${cf.fieldId}`] = !(shouldExpandAllForSearch || cfActive > 0);
    }
    setCollapsedSections(collapsed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (query.trim().length === 0) return;
    setCollapsedSections(prev => {
      const next: Record<string, boolean> = {};
      for (const key of Object.keys(prev)) next[key] = false;
      return next;
    });
  }, [open, query]);

  const toggleFilterValue = (type: SectionKey, value: string) => {
    const currentValues = filters[type];
    const exists = currentValues.includes(value);
    let next: string[];

    if (exists) {
      next = currentValues.filter((v) => v !== value);
    } else {
      const allValues = [...currentValues, value];
      const optionValues = availableOptions[type].map((opt) => opt.value);
      next = optionValues.filter((v) => allValues.includes(v));
    }

    onFilterChange({ [type]: next });
  };

  const clearFilter = (type: SectionKey) => {
    onFilterChange({ [type]: [] });
  };

  const clearAllFilters = () => {
    onFilterChange({
      status: [],
      assignees: [],
      tags: [],
      priority: [],
      customFields: {},
      customFieldText: {},
      customFieldDateRange: {},
    });
  };

  const filterSections = [
    {
      key: 'status' as const,
      label: 'Stato',
      icon: <CircleDashed className="w-3.5 h-3.5 text-text-tertiary" />,
    },
    {
      key: 'assignees' as const,
      label: 'Assegnatari',
      icon: <User className="w-3.5 h-3.5 text-text-tertiary" />,
    },
    {
      key: 'tags' as const,
      label: 'Tag',
      icon: <Tag className="w-3.5 h-3.5 text-text-tertiary" />,
    },
    {
      key: 'priority' as const,
      label: 'Priorità',
      icon: <Flag className="w-3.5 h-3.5 text-text-tertiary" />,
    },
  ];

  const toggleSection = (key: SectionKey) => {
    setCollapsedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      <div ref={containerRef} className={compact ? 'relative' : 'flex-1 min-w-0 w-[120px] sm:w-[150px]'}>
        {compact ? (
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            title="Filtra"
            className={clsx(
              'w-7 h-7 flex items-center justify-center rounded-md border transition-colors',
              totalActiveFilters > 0
                ? 'border-accent-violet/50 bg-accent-violet/10 text-accent-violet'
                : 'border-border-primary/80 bg-surface-tertiary/70 hover:bg-surface-hover text-text-tertiary'
            )}
          >
            {icon}
            {totalActiveFilters > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-accent-violet text-white text-[9px] font-bold flex items-center justify-center pointer-events-none">
                {totalActiveFilters}
              </span>
            )}
          </button>
        ) : (
        <div className="flex items-center w-full min-h-[36px] rounded-md border border-border-primary/80 bg-surface-tertiary/70 hover:bg-surface-hover transition-colors">
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="flex items-center gap-2 px-2 py-1.5 w-full text-left min-w-0"
          >
            {icon && <span className="text-text-tertiary shrink-0">{icon}</span>}
            <div className="flex flex-col items-start min-w-0 flex-1">
              <span className="text-[9px] uppercase tracking-wide text-text-quaternary font-semibold leading-none">
                Filtra
              </span>
              <span className="text-[10px] sm:text-[11px] text-text-secondary font-semibold leading-tight truncate whitespace-nowrap">
                {totalActiveFilters === 0
                  ? 'Nessun filtro'
                  : `${totalActiveFilters} filtro${totalActiveFilters > 1 ? 'i' : ''}`}
              </span>
            </div>
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clearAllFilters();
            }}
            className={clsx(
              'w-8 h-[36px] flex items-center justify-center rounded-md hover:bg-surface-hover text-text-quaternary hover:text-accent-rose transition-colors shrink-0',
              totalActiveFilters > 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'
            )}
            title="Rimuovi tutti i filtri"
            aria-label="Rimuovi tutti i filtri"
          >
            <X className="w-3 h-3" />
          </button>
          <button
            type="button"
            onClick={() => setOpen((prev) => !prev)}
            className="w-8 h-[36px] flex items-center justify-center rounded-md hover:bg-surface-hover text-text-quaternary transition-colors shrink-0"
            aria-label={open ? 'Chiudi filtri' : 'Apri filtri'}
          >
            <ChevronDown className={clsx('w-3 h-3 transition-transform', open && 'rotate-180')} />
          </button>
        </div>
        )}
      </div>

      {open &&
        dropdownPosition &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-50 animate-scale-in origin-top"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
          >
            <DropdownShell widthClass="min-w-[260px] max-w-[380px]">
              <div className="flex items-center gap-2 px-2.5 py-1.5 border-b border-border-primary">
                <Search className="w-3.5 h-3.5 text-text-quaternary shrink-0" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="flex-1 bg-transparent text-[11px] text-text-primary placeholder:text-text-quaternary focus:outline-none"
                  placeholder="Cerca filtri..."
                  autoFocus
                />
                {query && (
                  <button
                    onClick={() => setQuery('')}
                    className="p-1 rounded hover:bg-surface-hover text-text-quaternary"
                    title="Cancella ricerca"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              <div className="max-h-[60vh] overflow-y-auto">
                {filterSections.map((section) => {
                  const options = availableOptions[section.key];
                  const activeValues = filters[section.key];

                  // For tags with scoped groups, compute options from groups
                  const useTagGroups = section.key === 'tags' && tagGroups;
                  const allTagGroupOptions = useTagGroups
                    ? tagGroups!.flatMap(g => g.options)
                    : [];
                  const effectiveOptions = useTagGroups ? allTagGroupOptions : options;

                  const filteredOptions = query
                    ? effectiveOptions.filter((opt) => {
                        const searchLower = query.toLowerCase();
                        return (
                          opt.label.toLowerCase().includes(searchLower) ||
                          opt.value.toLowerCase().includes(searchLower)
                        );
                      })
                    : effectiveOptions;

                  if (filteredOptions.length === 0) return null;

                  const isCollapsed = collapsedSections[section.key];
                  const activeOptionByValue = new Map(options.map((o) => [o.value, o]));
                  const activeOptions = activeValues
                    .map((v) => activeOptionByValue.get(v))
                    .filter(Boolean) as Array<{ value: string; label: string; icon?: ReactNode }>;

                  return (
                    <div
                      key={section.key}
                      className="border-b border-border-primary/60 last:border-b-0"
                    >
                      <div className="flex items-center justify-between px-2.5 py-1 relative">
                        <button
                          type="button"
                          onClick={() => toggleSection(section.key)}
                          className="flex items-center min-w-0 flex-1 hover:text-text-primary transition-colors gap-2"
                          title={isCollapsed ? 'Espandi' : 'Comprimi'}
                        >
                          <span className="w-3 h-3 shrink-0 flex items-center justify-center">
                            <ChevronRight
                              className={clsx(
                                'w-3 h-3 text-text-quaternary transition-transform origin-center',
                                !isCollapsed && 'rotate-90'
                              )}
                            />
                          </span>
                          <span className="w-4 h-4 shrink-0 flex items-center justify-center">
                            {section.icon}
                          </span>
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide truncate text-left min-w-0 max-w-[140px] sm:max-w-[170px] leading-none">
                              {section.label}
                            </span>
                            <span className="text-[10px] text-text-quaternary font-medium tabular-nums shrink-0 leading-none">
                              {filteredOptions.length}
                            </span>
                            <span
                              className={clsx(
                                'h-4 flex items-center justify-center px-1.5 rounded text-[9px] font-semibold bg-accent-violet/20 text-accent-violet shrink-0 leading-none',
                                activeValues.length > 0 ? 'opacity-100' : 'opacity-0'
                              )}
                              aria-hidden={activeValues.length === 0}
                            >
                              {activeValues.length || 0}
                            </span>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => clearFilter(section.key)}
                          className={clsx(
                            'w-[56px] text-right text-[9px] text-text-quaternary hover:text-accent-rose transition-colors shrink-0',
                            activeValues.length > 0
                              ? 'opacity-100'
                              : 'opacity-0 pointer-events-none'
                          )}
                          aria-label={`Cancella filtri ${section.label}`}
                        >
                          Cancella
                        </button>
                      </div>

                      <div className="space-y-0.5 px-1.5">
                        {/* Tags section: grouped rendering when tagGroups are available */}
                        {section.key === 'tags' && tagGroups && !isCollapsed ? (
                          tagGroups.map((group) => {
                            const groupOpts = query
                              ? group.options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
                              : group.options;
                            if (groupOpts.length === 0) return null;
                            return (
                              <div key={group.label}>
                                <div className="px-2 pt-1.5 pb-0.5 text-[9px] uppercase tracking-wider text-text-quaternary font-semibold">
                                  {group.label}
                                </div>
                                {groupOpts.map((opt) => {
                                  const isSelected = activeValues.includes(opt.value);
                                  return (
                                    <button
                                      key={opt.value}
                                      onClick={() => toggleFilterValue(section.key, opt.value)}
                                      className={clsx(
                                        'w-full flex items-center gap-2 px-2 py-1 text-left text-[11px] hover:bg-surface-hover transition-colors rounded-md min-h-[28px]',
                                        isSelected && 'bg-surface-hover/80'
                                      )}
                                    >
                                      <span className="w-4 shrink-0 flex items-center justify-center text-text-tertiary">
                                        {opt.icon ?? <Tag className="w-3 h-3 text-accent-violet" />}
                                      </span>
                                      <span className={clsx('flex-1 truncate', isSelected ? 'text-accent-violet font-medium' : 'text-text-secondary')}>
                                        {opt.label}
                                      </span>
                                      <span className="w-4 shrink-0 flex items-center justify-center">
                                        <Check className={clsx('w-3.5 h-3.5 text-accent-violet', isSelected ? 'opacity-100' : 'opacity-0')} />
                                      </span>
                                    </button>
                                  );
                                })}
                              </div>
                            );
                          })
                        ) : (
                        (isCollapsed ? activeOptions : filteredOptions).map((opt) => {
                          const isSelected = activeValues.includes(opt.value);
                          return (
                            <button
                              key={opt.value}
                              onClick={() => toggleFilterValue(section.key, opt.value)}
                              className={clsx(
                                'w-full flex items-center gap-2 px-2 py-1 text-left text-[11px] hover:bg-surface-hover transition-colors rounded-md min-h-[28px]',
                                isSelected && 'bg-surface-hover/80'
                              )}
                            >
                              <span className="w-4 shrink-0 flex items-center justify-center text-text-tertiary">
                                {opt.icon ?? null}
                              </span>
                              <span
                                className={clsx(
                                  'flex-1 truncate',
                                  isSelected
                                    ? 'text-accent-violet font-medium'
                                    : 'text-text-secondary'
                                )}
                              >
                                {opt.label}
                              </span>
                              <span className="w-4 shrink-0 flex items-center justify-center">
                                <Check
                                  className={clsx(
                                    'w-3.5 h-3.5 text-accent-violet',
                                    isSelected ? 'opacity-100' : 'opacity-0'
                                  )}
                                />
                              </span>
                            </button>
                          );
                        })
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Custom field filter sections */}
                {customFieldSections.map((cfSection) => {
                  const sectionKey = `cf_${cfSection.fieldId}`;
                  const isCollapsed = collapsedSections[sectionKey] ?? true;

                  // Determine if this section has an active filter
                  let hasActiveFilter = false;
                  if (cfSection.type === 'text') {
                    hasActiveFilter = !!(customFieldTextFilters?.[cfSection.fieldId]);
                  } else if (cfSection.type === 'date') {
                    const dr = customFieldDateRangeFilters?.[cfSection.fieldId];
                    hasActiveFilter = !!(dr?.start || dr?.end);
                  } else {
                    hasActiveFilter = (customFieldFilters?.[cfSection.fieldId]?.length || 0) > 0;
                  }

                  // For text type: hide if searching and label doesn't match query
                  if (query && cfSection.type === 'text' && !cfSection.label.toLowerCase().includes(query.toLowerCase())) return null;
                  if (query && cfSection.type === 'date' && !cfSection.label.toLowerCase().includes(query.toLowerCase())) return null;

                  // For select types: filter options
                  const activeValues = (customFieldFilters?.[cfSection.fieldId] || []).map(String);
                  const filteredOptions = cfSection.type === 'dropdown' || cfSection.type === 'boolean'
                    ? (query
                        ? cfSection.options.filter((opt) => {
                            const searchLower = query.toLowerCase();
                            return opt.label.toLowerCase().includes(searchLower) || opt.value.toLowerCase().includes(searchLower);
                          })
                        : cfSection.options)
                    : [];

                  if (query && (cfSection.type === 'dropdown' || cfSection.type === 'boolean') && filteredOptions.length === 0) return null;

                  const activeOptionByValue = new Map(cfSection.options.map((o) => [o.value, o]));
                  const activeOptions = activeValues
                    .map((v) => activeOptionByValue.get(v))
                    .filter(Boolean) as Array<{ value: string; label: string; icon?: ReactNode }>;

                  const toggleCfValue = (value: string) => {
                    const current = (customFieldFilters?.[cfSection.fieldId] || []).map(String);
                    const exists = current.includes(value);
                    const next = exists ? current.filter((v) => v !== value) : [...current, value];
                    const updated = { ...(customFieldFilters || {}), [cfSection.fieldId]: next };
                    if (next.length === 0) delete updated[cfSection.fieldId];
                    onFilterChange({ customFields: updated });
                  };

                  const clearCfFilter = () => {
                    if (cfSection.type === 'text') {
                      const updated = { ...(customFieldTextFilters || {}) };
                      delete updated[cfSection.fieldId];
                      onFilterChange({ customFieldText: updated });
                    } else if (cfSection.type === 'date') {
                      const updated = { ...(customFieldDateRangeFilters || {}) };
                      delete updated[cfSection.fieldId];
                      onFilterChange({ customFieldDateRange: updated });
                    } else {
                      const updated = { ...(customFieldFilters || {}) };
                      delete updated[cfSection.fieldId];
                      onFilterChange({ customFields: updated });
                    }
                  };

                  const optionCount = cfSection.type === 'text' || cfSection.type === 'date'
                    ? undefined
                    : filteredOptions.length;

                  return (
                    <div
                      key={sectionKey}
                      className="border-b border-border-primary/60 last:border-b-0"
                    >
                      <div className="flex items-center justify-between px-2.5 py-1 relative">
                        <button
                          type="button"
                          onClick={() => setCollapsedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }))}
                          className="flex items-center min-w-0 flex-1 hover:text-text-primary transition-colors gap-2"
                          title={isCollapsed ? 'Espandi' : 'Comprimi'}
                        >
                          <span className="w-3 h-3 shrink-0 flex items-center justify-center">
                            <ChevronRight
                              className={clsx(
                                'w-3 h-3 text-text-quaternary transition-transform origin-center',
                                !isCollapsed && 'rotate-90'
                              )}
                            />
                          </span>
                          <span className="w-4 h-4 shrink-0 flex items-center justify-center">
                            <SlidersHorizontal className="w-3.5 h-3.5 text-text-tertiary" />
                          </span>
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-[10px] font-semibold text-text-secondary uppercase tracking-wide truncate text-left min-w-0 max-w-[140px] sm:max-w-[170px] leading-none">
                              {cfSection.label}
                            </span>
                            {optionCount !== undefined && (
                              <span className="text-[10px] text-text-quaternary font-medium tabular-nums shrink-0 leading-none">
                                {optionCount}
                              </span>
                            )}
                            <span
                              className={clsx(
                                'h-4 flex items-center justify-center px-1.5 rounded text-[9px] font-semibold bg-accent-violet/20 text-accent-violet shrink-0 leading-none',
                                hasActiveFilter ? 'opacity-100' : 'opacity-0'
                              )}
                              aria-hidden={!hasActiveFilter}
                            >
                              {cfSection.type === 'text' || cfSection.type === 'date' ? '1' : activeValues.length || 0}
                            </span>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={clearCfFilter}
                          className={clsx(
                            'w-[56px] text-right text-[9px] text-text-quaternary hover:text-accent-rose transition-colors shrink-0',
                            hasActiveFilter
                              ? 'opacity-100'
                              : 'opacity-0 pointer-events-none'
                          )}
                          aria-label={`Cancella filtri ${cfSection.label}`}
                        >
                          Cancella
                        </button>
                      </div>

                      {!isCollapsed && cfSection.type === 'text' && (
                        <div className="px-2.5 pb-1.5">
                          <input
                            type="text"
                            value={customFieldTextFilters?.[cfSection.fieldId] || ''}
                            onChange={(e) => {
                              const val = e.target.value;
                              const updated = { ...(customFieldTextFilters || {}), [cfSection.fieldId]: val };
                              if (!val) delete updated[cfSection.fieldId];
                              onFilterChange({ customFieldText: updated });
                            }}
                            placeholder="Cerca..."
                            className="w-full bg-surface-primary border border-border-primary rounded-md px-2 py-1 text-[11px] text-text-primary placeholder:text-text-quaternary focus:outline-none focus:border-accent-violet/50"
                          />
                        </div>
                      )}

                      {!isCollapsed && cfSection.type === 'date' && (
                        <div className="px-2.5 pb-1.5 flex items-center gap-1.5">
                          <InlineDatePicker
                            value={customFieldDateRangeFilters?.[cfSection.fieldId]?.start ? new Date(customFieldDateRangeFilters[cfSection.fieldId].start!).getTime() : null}
                            onChange={(ms) => {
                              const dateStr = ms ? new Date(ms).toISOString().slice(0, 10) : null;
                              const current = customFieldDateRangeFilters?.[cfSection.fieldId] || { start: null, end: null };
                              const updated = { ...(customFieldDateRangeFilters || {}), [cfSection.fieldId]: { ...current, start: dateStr } };
                              if (!updated[cfSection.fieldId].start && !updated[cfSection.fieldId].end) delete updated[cfSection.fieldId];
                              onFilterChange({ customFieldDateRange: updated });
                            }}
                            label="Da"
                            showTime={false}
                            size="sm"
                          />
                          <span className="text-[9px] text-text-quaternary shrink-0">—</span>
                          <InlineDatePicker
                            value={customFieldDateRangeFilters?.[cfSection.fieldId]?.end ? new Date(customFieldDateRangeFilters[cfSection.fieldId].end!).getTime() : null}
                            onChange={(ms) => {
                              const dateStr = ms ? new Date(ms).toISOString().slice(0, 10) : null;
                              const current = customFieldDateRangeFilters?.[cfSection.fieldId] || { start: null, end: null };
                              const updated = { ...(customFieldDateRangeFilters || {}), [cfSection.fieldId]: { ...current, end: dateStr } };
                              if (!updated[cfSection.fieldId].start && !updated[cfSection.fieldId].end) delete updated[cfSection.fieldId];
                              onFilterChange({ customFieldDateRange: updated });
                            }}
                            label="A"
                            showTime={false}
                            size="sm"
                          />
                        </div>
                      )}

                      {(cfSection.type === 'dropdown' || cfSection.type === 'boolean') && (
                        <div className="space-y-0.5 px-1.5">
                          {filteredOptions.length === 0 && !isCollapsed ? (
                            <div className="px-2 py-1.5 text-[10px] text-text-quaternary italic">
                              Nessun valore
                            </div>
                          ) : (isCollapsed ? activeOptions : filteredOptions).map((opt) => {
                            const isSelected = activeValues.includes(opt.value);
                            return (
                              <button
                                key={opt.value}
                                onClick={() => toggleCfValue(opt.value)}
                                className={clsx(
                                  'w-full flex items-center gap-2 px-2 py-1 text-left text-[11px] hover:bg-surface-hover transition-colors rounded-md min-h-[28px]',
                                  isSelected && 'bg-surface-hover/80'
                                )}
                              >
                                <span className="w-4 shrink-0 flex items-center justify-center text-text-tertiary">
                                  {opt.icon ?? null}
                                </span>
                                <span
                                  className={clsx(
                                    'flex-1 truncate',
                                    isSelected
                                      ? 'text-accent-violet font-medium'
                                      : 'text-text-secondary'
                                  )}
                                >
                                  {opt.label}
                                </span>
                                <span className="w-4 shrink-0 flex items-center justify-center">
                                  <Check
                                    className={clsx(
                                      'w-3.5 h-3.5 text-accent-violet',
                                      isSelected ? 'opacity-100' : 'opacity-0'
                                    )}
                                  />
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </DropdownShell>
          </div>,
          document.body
        )}
    </>
  );
}
